import React from "react";
import { Chip, Dot, Mono, BatonTrack, fmtD, relDays } from "./ui";

// "By stage": one column per custody stage, a card per pallet
export function PalletBoard({ pallets, stages, reportYear, onOpen }) {
    return (
        <div className="overflow-x-auto pb-2">
            <div className="grid gap-3 min-w-[1100px]" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}>
                {stages.map(st => {
                    const here = pallets.filter(p => p.custody?.stage === st.key);
                    return (
                        <section key={st.key} aria-label={st.label} className="bg-[#111827] border border-[#1F2937] rounded-xl p-2.5 min-h-[260px] flex flex-col gap-2">
                            <header className="px-1 pb-2 border-b border-[#1F2937]">
                                <p className="text-[12px] font-bold text-white">{st.label}</p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">{st.key === "CUSTOMER_GR" ? "Customer" : st.party}</p>
                            </header>
                            {here.length === 0 && <p className="px-1 text-[11px] text-slate-600">No pallets</p>}
                            {here.map(p => (
                                <button key={p.id} onClick={() => onOpen(p.id)}
                                    className="text-left bg-[#0D1520] border border-[#1F2937] hover:border-slate-500 rounded-lg p-2.5 space-y-1 transition-colors">
                                    <span className="flex items-center gap-1.5"><Dot health={p.popup?.health}/><b className="text-[12px] text-white truncate">{p.customer}</b></span>
                                    <span className="block text-[11px] text-slate-300 truncate">{p.program}</span>
                                    <span className="block text-[11px] text-slate-400">With <b className="text-slate-200">{p.custody.sitsWith}</b>
                                        {p.custody.source === "manual" && <span className="text-blue-300"> · handed off</span>}</span>
                                    <span className="block text-[10px] text-slate-500 leading-snug">Waiting for {p.custody.waitingFor}</span>
                                    <span className="flex items-center justify-between pt-1">
                                        {p.custody.daysLate > 0 ? <Chip tone="bad">{p.custody.daysLate}d late</Chip> : <Chip>on time</Chip>}
                                        <span className="text-[10px] text-slate-500">DC {fmtD(p.dcDate, reportYear)}</span>
                                    </span>
                                </button>
                            ))}
                        </section>
                    );
                })}
            </div>
        </div>
    );
}

// "Table": one row per pallet
export function PalletTable({ pallets, reportDate, reportYear, commentCounts, onOpen }) {
    const TH = "px-3 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap";
    return (
        <div className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-x-auto">
            <table className="w-full text-left text-xs">
                <thead className="bg-[#0D1520]">
                    <tr>{["", "Pallet", "Stage", "Sits with", "Waiting for", "DC date", "Issues", "Notes"].map(h => <th key={h} className={TH}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1F2937]">
                    {pallets.map(p => (
                        <tr key={p.id} tabIndex={0} onClick={() => onOpen(p.id)} onKeyDown={e => e.key === "Enter" && onOpen(p.id)}
                            className="hover:bg-[#1F2937]/40 cursor-pointer align-top">
                            <td className="px-3 py-3"><Dot health={p.popup?.health}/></td>
                            <td className="px-3 py-3">
                                <b className="text-slate-100">{p.customer}</b> <span className="text-slate-400">· {p.program}</span>
                                <div className="text-[10px] text-slate-500"><Mono>{p.plantCode}</Mono> · {p.palletQty} pallets · {p.bom?.components} SKUs</div>
                            </td>
                            <td className="px-3 py-3 min-w-[160px]"><BatonTrack custody={p.custody} compact/><div className="text-[10px] text-slate-500 mt-1">{p.custody.stageLabel}</div></td>
                            <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{p.custody.sitsWith}
                                {p.custody.source === "manual" && <div className="text-[10px] text-blue-300">handed off</div>}</td>
                            <td className="px-3 py-3 text-slate-400 max-w-[280px]">{p.custody.waitingFor}
                                {p.custody.daysLate > 0 && <> <Chip tone="bad">{p.custody.daysLate}d late</Chip></>}</td>
                            <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{fmtD(p.dcDate, reportYear)}<div className="text-[10px] text-slate-500">{relDays(p.dcDate, reportDate)}</div></td>
                            <td className="px-3 py-3 text-slate-300 tabular-nums">{p.popup?.openIssues ?? 0}</td>
                            <td className="px-3 py-3 text-slate-300 tabular-nums">{commentCounts[p.id] || 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
